/**
 * FERP Portal - Main Entry Point
 *
 * Domain-agnostic, production-ready portal leveraging:
 * - Three-Layer Authentication: Authentik OIDC → AWS STS → IAM Policy
 * - 25-Primitive System: Complete 5×5 grid (Design, Configure, Execute, Monitor, Optimize)
 * - DSL-Driven Rendering: Subgraphs generate templates; FERP Core renders generically
 * - GraphQL Codegen: Type-safe queries and mutations
 * - Repository Pattern 2.0: Facades over generated hooks with tenant injection
 */

// MUST be first: Browser polyfills for Node.js APIs
import './polyfills';

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { MantineProvider } from '@mantine/core';
import { Notifications } from '@mantine/notifications';
import { ModalsProvider } from '@mantine/modals';
import { ApolloProvider } from '@apollo/client/react';

import { App } from './App';
import { createApolloClient } from './utils/apollo-client';
import { theme } from './styles/theme';

// Import Mantine styles
import '@mantine/core/styles.css';
import '@mantine/notifications/styles.css';
import '@mantine/dates/styles.css';
import './styles/global.css';

// Register service worker (DRR-150 Phase 1)
// Runs after page load so it never delays first paint.
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register('/sw.js', { scope: '/' })
      .then(() => {
        // Request persistent storage so Chrome doesn't evict Automerge OPFS
        // data under storage pressure (DRR-150 Phase 2 requirement).
        if ('storage' in navigator && 'persist' in navigator.storage) {
          navigator.storage.persist();
        }
      })
      .catch(() => {
        // SW registration failure is non-fatal — app works online without it.
      });
  });
}

// Create Apollo Client
const apolloClient = createApolloClient();

// Mount application
const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error('Root element not found. Check index.html for div#root.');
}

createRoot(rootElement).render(
  <StrictMode>
    <ApolloProvider client={apolloClient}>
      <MantineProvider theme={theme} defaultColorScheme="auto">
        <Notifications position="top-right" />
        <ModalsProvider>
          <App />
        </ModalsProvider>
      </MantineProvider>
    </ApolloProvider>
  </StrictMode>
);
